// ============================================================================
// GESTIMMONY — Page Tableau de bord (/app)
// ============================================================================
// Vue d'ensemble du parc locatif du bailleur connecté :
//   - 4 cartes KPI (parc, occupation, encaissé du mois, impayés)
//   - Graphique 6 mois : encaissé (barres) vs attendu (ligne pointillée)
//   - Donut de répartition du parc (loué / vacant / travaux)
//   - Échéances de bail < 90 jours, activité récente, alertes prioritaires
//   - Top 3 des biens par revenus encaissés depuis janvier
//
// Données : 2 appels API en parallèle au montage
//   GET /api/dashboard/stats   → tous les indicateurs calculés côté backend
//   GET /api/dashboard/alerts  → alertes prioritaires (baux expirants, impayés)
// ============================================================================
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, fmtEUR, fmtDate } from "@/lib/api";   // api = client axios préconfiguré (cookies JWT)
import { useAuth } from "@/contexts/AuthContext";    // utilisateur connecté (nom pour le "Bonjour")
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import {
  Building2, Home, TrendingUp, TrendingDown, AlertTriangle, Wallet,
  Receipt as ReceiptIcon, Wrench, PlusCircle, ArrowRight, FileText,
  Sparkles, CircleDot, Clock, Trophy, Users, Compass,
} from "lucide-react";
// Recharts : bibliothèque de graphiques React (barres + lignes + camembert)
import {
  ResponsiveContainer, ComposedChart, Bar, Line, XAxis, YAxis, Tooltip,
  CartesianGrid, Legend, PieChart, Pie, Cell,
} from "recharts";
import { AlertsWidget } from "@/pages/Alerts";
import { startProductTour, maybeAutoStartTour } from "@/lib/tour";

// Couleurs fixes du donut de répartition (alignées sur la palette teal/amber)
const SPLIT_COLORS = { Loué: "#0F766E", Vacant: "#94A3B8", Travaux: "#F59E0B" };

// Petit badge "+12 %" / "-5 %" vert ou rouge selon le signe de la variation
function DeltaBadge({ value }) {
  if (value === null || value === undefined) return null;  // pas de référence → rien
  const positive = value >= 0;
  return (
    <span className={`inline-flex items-center gap-0.5 text-[11px] font-semibold px-1.5 py-0.5 rounded-md ${positive ? "bg-emerald-50 text-emerald-700" : "bg-rose-50 text-rose-700"}`}>
      {positive ? <TrendingUp className="h-3 w-3" /> : <TrendingDown className="h-3 w-3" />}
      {positive ? "+" : ""}{value}%
    </span>
  );
}

// Carte KPI réutilisable : libellé, grosse valeur, sous-texte, icône colorée,
// variation (delta) et lien d'action optionnel en bas
function KpiCard({ label, value, sub, icon: Icon, accent, delta, testid, cta }) {
  return (
    <Card data-testid={testid} className="card-hover p-5 bg-white border border-stone-200 shadow-none relative overflow-hidden">
      <div className="flex items-start justify-between">
        <div className="min-w-0">
          <div className="text-[11px] font-semibold uppercase tracking-wider text-slate-500">{label}</div>
          <div className="mt-2 flex items-baseline gap-2">
            <div className="font-heading text-2xl md:text-3xl font-bold tracking-tight text-slate-900">{value}</div>
            <DeltaBadge value={delta} />
          </div>
          {sub && <div className="mt-1 text-xs text-slate-500 truncate">{sub}</div>}
        </div>
        <div className={`h-9 w-9 rounded-lg flex items-center justify-center shrink-0 ${accent}`}>
          <Icon className="h-4 w-4" />
        </div>
      </div>
      {cta && (
        <Link to={cta.to} className="mt-3 inline-flex items-center gap-1 text-xs font-medium text-[hsl(var(--primary))] hover:gap-2 transition-all">
          {cta.label} <ArrowRight className="h-3 w-3" />
        </Link>
      )}
    </Card>
  );
}

export default function DashboardPage() {
  const { user } = useAuth();            // utilisateur connecté (pour personnaliser l'accueil)
  const [stats, setStats] = useState(null);    // indicateurs — null = pas encore chargés
  const [alerts, setAlerts] = useState([]);    // alertes prioritaires

  // Chargement initial : les 2 appels API partent en parallèle (Promise.all)
  // pour réduire le temps d'attente. useEffect avec [] = exécuté 1 seule fois.
  useEffect(() => {
    (async () => {
      try {
        const [s, a] = await Promise.all([
          api.get("/dashboard/stats"),
          api.get("/dashboard/alerts"),
        ]);
        setStats(s.data); setAlerts(a.data);
      } catch (e) { console.error(e); }
    })();
  }, []);

  // Visite guidée : démarre automatiquement à la première connexion du bailleur
  useEffect(() => {
    if (stats && user) maybeAutoStartTour(user);
  }, [stats, user]);

  // État de chargement : tant que stats est null, on affiche un placeholder
  if (!stats) return <div className="text-sm text-slate-500">Chargement du tableau de bord…</div>;

  // % de l'objectif annuel atteint (encaissé depuis janvier / attendu depuis janvier)
  const ytdCoverage = stats.expected_ytd > 0 ? Math.round((stats.encaisse_ytd / stats.expected_ytd) * 100) : 0;
  // % du loyer du mois en cours effectivement encaissé
  const monthCoverage = stats.expected_monthly > 0 ? Math.round((stats.encaisse_this_month / stats.expected_monthly) * 100) : 0;
  // Total de biens (pour masquer le donut si le parc est vide)
  const totalSplit = stats.portfolio_split.reduce((s, x) => s + x.value, 0);

  // Prénom de l'utilisateur pour le "Bonjour {prénom}" (fallback neutre)
  const hero = user?.name?.split(" ")[0] || "à vous";

  return (
    <div className="space-y-6" data-testid="dashboard-page">
      {/* Hero */}
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-3">
        <div>
          <div className="text-xs font-semibold uppercase tracking-wider text-[hsl(var(--primary))]">Tableau de bord</div>
          <h1 className="mt-1 font-heading text-2xl sm:text-3xl font-bold tracking-tight text-slate-900">
            Bonjour {hero} 👋
          </h1>
          <p className="mt-1 text-sm text-slate-500">
            Voici l'état de votre parc au {new Date().toLocaleDateString("fr-FR", { day: "numeric", month: "long", year: "numeric" })}.
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button variant="outline" size="sm" data-testid="dashboard-tour-btn" onClick={() => startProductTour()}>
            <Compass className="h-4 w-4 mr-1.5" /> Visite guidée
          </Button>
          <Link to="/app/biens">
            <Button variant="outline" size="sm"><PlusCircle className="h-4 w-4 mr-1.5" /> Nouveau bien</Button>
          </Link>
          <Link to="/app/quittances">
            <Button size="sm" className="bg-[hsl(var(--primary))]"><ReceiptIcon className="h-4 w-4 mr-1.5" /> Générer une quittance</Button>
          </Link>
        </div>
      </div>

      {/* Widget alertes intelligentes (Vague 3) */}
      <AlertsWidget />

      {/* KPIs */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <KpiCard
          testid="kpi-portfolio"
          label="Parc immobilier"
          value={stats.total_properties}
          sub={`${stats.loue} loués · ${stats.vacant} vacants · ${stats.travaux} en travaux`}
          icon={Building2}
          accent="bg-[hsl(var(--accent))] text-[hsl(var(--primary))]"
          cta={{ to: "/app/biens", label: "Voir mes biens" }}
        />
        <KpiCard
          testid="kpi-occupancy"
          label="Taux d'occupation"
          value={`${stats.occupancy_rate}%`}
          sub={`${stats.active_leases} baux actifs · ${stats.total_tenants} locataires`}
          icon={Home}
          accent="bg-emerald-50 text-emerald-700"
        />
        <KpiCard
          testid="kpi-encaisse"
          label="Encaissé ce mois"
          value={fmtEUR(stats.encaisse_this_month)}
          sub={`Attendu : ${fmtEUR(stats.expected_monthly)} · ${monthCoverage}%`}
          icon={Wallet}
          accent="bg-blue-50 text-blue-700"
          delta={stats.delta_encaisse_pct}
        />
        <KpiCard
          testid="kpi-impayes"
          label="Impayés"
          value={stats.impayes_count}
          sub={`${fmtEUR(stats.impayes_total)} à recouvrer`}
          icon={AlertTriangle}
          accent={stats.impayes_count ? "bg-rose-50 text-rose-700" : "bg-slate-50 text-slate-500"}
          cta={stats.impayes_count ? { to: "/app/quittances", label: "Voir les impayés" } : null}
        />
      </div>

      {/* Chart + Occupation */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <Card className="lg:col-span-2 p-5 border-stone-200 shadow-none" data-testid="revenue-chart">
          <div className="flex items-start justify-between mb-4 flex-wrap gap-2">
            <div>
              <div className="text-[11px] font-semibold uppercase tracking-wider text-slate-500">Recettes vs objectif</div>
              <div className="font-heading text-lg font-semibold text-slate-900 mt-1">6 derniers mois</div>
            </div>
            <div className="flex items-center gap-3 text-xs">
              <div className="flex items-center gap-1.5"><span className="h-2.5 w-2.5 rounded-sm bg-[#0F766E]" /> Encaissé</div>
              <div className="flex items-center gap-1.5"><span className="h-2.5 w-0.5 bg-[#F59E0B] border-t-2 border-dashed border-[#F59E0B] w-4" /> Attendu</div>
            </div>
          </div>
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <ComposedChart data={stats.monthly_history} margin={{ top: 5, right: 10, left: -10, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#E7E5E4" vertical={false} />
                <XAxis dataKey="period" tickLine={false} axisLine={false} tick={{ fontSize: 11, fill: "#64748B" }}
                  tickFormatter={(v) => v?.slice(5) + "/" + v?.slice(2,4)} />
                <YAxis tickLine={false} axisLine={false} tick={{ fontSize: 11, fill: "#64748B" }} />
                <Tooltip
                  contentStyle={{ borderRadius: 8, border: "1px solid #E7E5E4", fontSize: 12 }}
                  formatter={(v) => fmtEUR(v)}
                />
                <Bar dataKey="encaisse" name="Encaissé" fill="#0F766E" radius={[6, 6, 0, 0]} maxBarSize={44} />
                <Line dataKey="attendu" name="Attendu" stroke="#F59E0B" strokeWidth={2.5} strokeDasharray="5 4" dot={{ r: 3, fill: "#F59E0B" }} />
              </ComposedChart>
            </ResponsiveContainer>
          </div>

          {/* YTD summary bar */}
          <div className="mt-4 pt-4 border-t border-stone-100">
            <div className="flex items-center justify-between mb-1.5">
              <div className="text-xs font-semibold text-slate-700">Encaissement {stats.year}</div>
              <div className="text-xs text-slate-500">
                <span className="font-semibold text-slate-900">{fmtEUR(stats.encaisse_ytd)}</span> / {fmtEUR(stats.expected_ytd)}
              </div>
            </div>
            <Progress value={Math.min(ytdCoverage, 100)} className="h-2" />
            <div className="mt-1 text-[11px] text-slate-500">{ytdCoverage}% de l'objectif annuel atteint</div>
          </div>
        </Card>

        {/* Occupation donut */}
        <Card className="p-5 border-stone-200 shadow-none" data-testid="occupation-chart">
          <div className="text-[11px] font-semibold uppercase tracking-wider text-slate-500">Statut du parc</div>
          <div className="font-heading text-lg font-semibold text-slate-900 mt-1">Répartition</div>
          {totalSplit === 0 ? (
            <div className="text-sm text-slate-500 py-10 text-center">Ajoutez un bien pour voir cette vue.</div>
          ) : (
            <>
              <div className="relative h-52 mt-2">
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie data={stats.portfolio_split} dataKey="value" nameKey="name" innerRadius={54} outerRadius={80} paddingAngle={3}>
                      {stats.portfolio_split.map((s, i) => (
                        <Cell key={i} fill={SPLIT_COLORS[s.name] || "#94A3B8"} />
                      ))}
                    </Pie>
                    <Tooltip formatter={(v, n) => [`${v} bien${v > 1 ? "s" : ""}`, n]} contentStyle={{ borderRadius: 8, border: "1px solid #E7E5E4", fontSize: 12 }} />
                  </PieChart>
                </ResponsiveContainer>
                <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                  <div className="text-center">
                    <div className="font-heading text-2xl font-bold">{stats.occupancy_rate}%</div>
                    <div className="text-[10px] uppercase tracking-wider text-slate-500">occupation</div>
                  </div>
                </div>
              </div>
              <div className="space-y-1.5 mt-2">
                {stats.portfolio_split.map((s) => (
                  <div key={s.name} className="flex items-center justify-between text-xs">
                    <div className="flex items-center gap-2">
                      <div className="h-2.5 w-2.5 rounded-full" style={{ background: SPLIT_COLORS[s.name] || "#94A3B8" }} />
                      <span className="text-slate-600">{s.name}</span>
                    </div>
                    <span className="font-semibold">{s.value}</span>
                  </div>
                ))}
              </div>
            </>
          )}
        </Card>
      </div>

      {/* Row 3: Upcoming leases + Activity + Alerts */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* Prochaines échéances */}
        <Card className="p-5 border-stone-200 shadow-none" data-testid="upcoming-leases">
          <div className="flex items-center justify-between mb-4">
            <div>
              <div className="text-[11px] font-semibold uppercase tracking-wider text-slate-500">Échéances de bail</div>
              <div className="font-heading text-lg font-semibold text-slate-900 mt-1">Dans les 90 jours</div>
            </div>
            <Clock className="h-4 w-4 text-slate-400" />
          </div>
          <div className="space-y-2.5 max-h-64 overflow-auto pr-1">
            {stats.upcoming_leases.length === 0 && (
              <div className="text-sm text-slate-500 py-6 text-center">Aucune échéance imminente. ✓</div>
            )}
            {stats.upcoming_leases.map((u, i) => (
              <div key={i} className="flex items-start gap-3 p-2.5 rounded-lg hover:bg-stone-50 transition-colors">
                <div className={`h-8 w-8 rounded-lg flex items-center justify-center shrink-0 ${
                  u.days < 30 ? "bg-rose-50 text-rose-700" : u.days < 60 ? "bg-amber-50 text-amber-700" : "bg-blue-50 text-blue-700"
                }`}>
                  <FileText className="h-3.5 w-3.5" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium truncate">{u.property_label}</div>
                  <div className="text-xs text-slate-500 truncate">{u.tenant_name}</div>
                </div>
                <div className="text-right">
                  <div className={`text-xs font-semibold ${u.days < 30 ? "text-rose-700" : "text-slate-700"}`}>J-{u.days}</div>
                  <div className="text-[10px] text-slate-400">{fmtDate(u.end_date)}</div>
                </div>
              </div>
            ))}
          </div>
        </Card>

        {/* Activité récente */}
        <Card className="p-5 border-stone-200 shadow-none" data-testid="activity-feed">
          <div className="flex items-center justify-between mb-4">
            <div>
              <div className="text-[11px] font-semibold uppercase tracking-wider text-slate-500">Activité</div>
              <div className="font-heading text-lg font-semibold text-slate-900 mt-1">Récente</div>
            </div>
            <Sparkles className="h-4 w-4 text-slate-400" />
          </div>
          <div className="space-y-2.5 max-h-64 overflow-auto pr-1">
            {stats.recent_activity.length === 0 && (
              <div className="text-sm text-slate-500 py-6 text-center">Rien à afficher pour le moment.</div>
            )}
            {stats.recent_activity.map((a, i) => (
              <div key={i} className="flex items-start gap-3">
                {a.type === "receipt" ? (
                  <div className={`h-8 w-8 rounded-lg flex items-center justify-center shrink-0 ${
                    a.status === "paye" ? "bg-emerald-50 text-emerald-700" : "bg-amber-50 text-amber-700"
                  }`}>
                    <ReceiptIcon className="h-3.5 w-3.5" />
                  </div>
                ) : (
                  <div className={`h-8 w-8 rounded-lg flex items-center justify-center shrink-0 ${
                    a.status === "resolu" ? "bg-emerald-50 text-emerald-700" :
                    a.priority === "haute" ? "bg-rose-50 text-rose-700" : "bg-blue-50 text-blue-700"
                  }`}>
                    <Wrench className="h-3.5 w-3.5" />
                  </div>
                )}
                <div className="flex-1 min-w-0">
                  {a.type === "receipt" ? (
                    <>
                      <div className="text-sm">
                        <span className="font-medium">{a.status === "paye" ? "Quittance payée" : a.status === "partiel" ? "Reçu partiel" : "En attente"}</span>{" "}
                        <span className="text-slate-500">— {a.period}</span>
                      </div>
                      <div className="text-xs text-slate-500 truncate">{a.property_label} · {a.tenant_name} · {fmtEUR(a.amount)}</div>
                    </>
                  ) : (
                    <>
                      <div className="text-sm font-medium truncate">{a.title}</div>
                      <div className="text-xs text-slate-500 truncate">{a.property_label} · {a.status} · {fmtEUR(a.cost)}</div>
                    </>
                  )}
                </div>
                <div className="text-[10px] text-slate-400 shrink-0">{a.date?.slice(0, 10)}</div>
              </div>
            ))}
          </div>
        </Card>

        {/* Alertes */}
        <Card className="p-5 border-stone-200 shadow-none" data-testid="dashboard-alerts">
          <div className="flex items-center justify-between mb-4">
            <div>
              <div className="text-[11px] font-semibold uppercase tracking-wider text-slate-500">Alertes prioritaires</div>
              <div className="font-heading text-lg font-semibold text-slate-900 mt-1">À traiter</div>
            </div>
            <Badge variant="outline" className="text-xs">{alerts.length}</Badge>
          </div>
          <div className="space-y-3 max-h-64 overflow-auto pr-1">
            {alerts.length === 0 && (
              <div className="text-sm text-slate-500 py-6 text-center">
                Aucune alerte. Tout est à jour. ✓
              </div>
            )}
            {alerts.map((a, i) => (
              <div
                key={i}
                data-testid={`alert-${i}`}
                className={`rounded-lg border px-3 py-2.5 text-sm ${
                  a.severity === "error"
                    ? "bg-rose-50 border-rose-200 text-rose-900"
                    : "bg-amber-50 border-amber-200 text-amber-900"
                }`}
              >
                <div className="flex items-start gap-2">
                  <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />
                  <div className="flex-1">
                    <div className="text-xs font-medium leading-relaxed">{a.message}</div>
                    <div className="text-[10px] opacity-75 mt-0.5">{a.date?.slice(0, 10)}</div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </Card>
      </div>

      {/* Top biens */}
      {stats.top_properties.length > 0 && (
        <Card className="p-5 border-stone-200 shadow-none" data-testid="top-properties">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <Trophy className="h-4 w-4 text-amber-500" />
              <div>
                <div className="text-[11px] font-semibold uppercase tracking-wider text-slate-500">Top biens</div>
                <div className="font-heading text-lg font-semibold text-slate-900">Meilleurs contributeurs {stats.year}</div>
              </div>
            </div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            {stats.top_properties.map((p, i) => (
              <div key={i} className={`rounded-lg p-4 border ${i === 0 ? "border-amber-300 bg-amber-50/50" : "border-stone-200"}`}>
                <div className="flex items-center gap-2 text-[11px] font-semibold uppercase tracking-wider text-slate-500">
                  <CircleDot className="h-3 w-3" /> #{i + 1}
                </div>
                <div className="mt-1 font-medium text-sm">{p.label}</div>
                <div className="text-xs text-slate-500">{p.city}</div>
                <div className="mt-2 font-heading font-bold text-lg text-[hsl(var(--primary))]">{fmtEUR(p.revenue_ytd)}</div>
                <div className="text-[10px] text-slate-400">encaissés depuis janvier</div>
              </div>
            ))}
          </div>
        </Card>
      )}
    </div>
  );
}
